/**
 * Repository Connection Service
 *
 * Manages OAuth-based connections for repository access (Azure DevOps, GitHub).
 * Uses the same APIHub connection/consent infrastructure as the Logic Apps
 * designer, but scoped to the automation project's environment.
 *
 * Key differences from standard connector OAuth:
 * - Connector names are custom per-project (e.g., `azurelogicappsado`, `ottogithub`)
 * - The `environment` for the connection uses `automationProjectId` (not the subscription)
 * - connectorType is set to "azuredevops" or "github" in the repository settings
 */

import type { RepoConnectorType } from '@/types/sandbox';
import { isFeatureEnabled } from '@/featureFlags';
import {
  createConnection,
  testConnection,
  type ConnectionResource,
  type TestConnectionResult,
} from './connectionApi';
import { listConsentLinks, confirmConsentCode } from './connectionApi';
import { AppContext } from './appClient';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Configuration for a repository OAuth connector.
 */
export interface RepoConnectorConfig {
  /** Connector type identifier */
  connectorType: RepoConnectorType;
  /** APIHub connector API name (e.g., 'azurelogicappsado') */
  apiName: string;
  /** Display name for UI */
  displayName: string;
  /** Icon name from Fluent UI (informational) */
  iconName: string;
}

/**
 * Parameters for creating a repository OAuth connection.
 */
export interface CreateRepoConnectionParams {
  /** Application context */
  ctx: AppContext;
  /** Connector config to use */
  connector: RepoConnectorConfig;
  /** Automation project ID (used as environment scope) */
  automationProjectId: string;
  /** Unique connection name */
  connectionName: string;
}

/**
 * Parameters for initiating repo OAuth consent.
 */
export interface RepoConsentParams {
  /** Application context */
  ctx: AppContext;
  /** Connection name (must already exist) */
  connectionName: string;
  /** OAuth redirect URL */
  redirectUrl: string;
  /** Azure AD object ID */
  objectId: string;
  /** Azure AD tenant ID */
  tenantId: string;
}

/**
 * Parameters for confirming repo OAuth consent.
 */
export interface RepoConfirmConsentParams {
  /** Application context */
  ctx: AppContext;
  /** Connection name */
  connectionName: string;
  /** Authorization code from OAuth callback */
  code: string;
  /** Azure AD object ID */
  objectId: string;
  /** Azure AD tenant ID */
  tenantId: string;
}

// =============================================================================
// CONNECTOR REGISTRY
// =============================================================================



/**
 * Default connector API names. These can be overridden by app settings
 * (VITE_ADO_CONNECTOR_API_NAME / VITE_GITHUB_CONNECTOR_API_NAME).
 * ADO uses the managed connector name 'azurelogicappsado'.
 * GitHub uses 'ottogithub' (custom connector published per-project).
 */
const DEFAULT_ADO_CONNECTOR_API = 'azurelogicappsado';
const LEGACY_ADO_CONNECTOR_API = 'ottoazuredevops';
const DEFAULT_GITHUB_CONNECTOR_API = 'ottogithub';

function getConnectorApiName(envValue: string | undefined, defaultValue: string): string {
  const normalizedEnvValue = envValue?.trim();
  if (!normalizedEnvValue) {
    return defaultValue;
  }

  const loweredValue = normalizedEnvValue.toLowerCase();
  if (loweredValue === 'undefined' || loweredValue === 'null') {
    return defaultValue;
  }

  return normalizedEnvValue;
}

/**
 * Get the ADO connector API name (configurable via env var).
 */
export function getAdoConnectorApiName(): string {
  const configuredApiName = getConnectorApiName(import.meta.env.VITE_ADO_CONNECTOR_API_NAME, '');
  if (configuredApiName) {
    return configuredApiName;
  }

  if (isFeatureEnabled('legacyAdoConnectorApi')) {
    return LEGACY_ADO_CONNECTOR_API;
  }

  return DEFAULT_ADO_CONNECTOR_API;
}

/**
 * Get the GitHub connector API name (configurable via env var).
 */
export function getGitHubConnectorApiName(): string {
  return getConnectorApiName(import.meta.env.VITE_GITHUB_CONNECTOR_API_NAME, DEFAULT_GITHUB_CONNECTOR_API);
}

/**
 * Pre-defined connector configurations for supported repository providers.
 */
export const REPO_CONNECTORS: Record<RepoConnectorType, RepoConnectorConfig> = {
  azuredevops: {
    connectorType: 'azuredevops',
    apiName: getAdoConnectorApiName(),
    displayName: 'Azure DevOps',
    iconName: 'AzureDevOps',
  },
  github: {
    connectorType: 'github',
    apiName: getGitHubConnectorApiName(),
    displayName: 'GitHub',
    iconName: 'GitHub',
  },
};

/**
 * Get the connector config for a given connector type.
 */
export function getRepoConnector(type: RepoConnectorType): RepoConnectorConfig {
  return REPO_CONNECTORS[type];
}

// =============================================================================
// CONNECTOR ID HELPERS
// =============================================================================

/**
 * Build the connector ID for a managed API connector.
 * Uses the short format expected by the sourceControlConnections routes:
 *   /managedApis/{apiName}
 */
export function buildConnectorId(apiName: string): string {
  return `/managedApis/${encodeURIComponent(apiName)}`;
}

/**
 * Generate a unique connection name for a repo connection.
 */
export function generateRepoConnectionName(
  connectorType: RepoConnectorType,
  suffix?: string,
): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  const base = connectorType === 'azuredevops' ? 'ado' : 'gh';
  return suffix
    ? `${base}-${suffix}-${timestamp}`
    : `${base}-conn-${timestamp}-${random}`;
}

// =============================================================================
// CONNECTION OPERATIONS
// =============================================================================

/**
 * Create a repository OAuth connection resource (Step 1 of OAuth flow).
 *
 * The connection is created without authentication — the user must
 * complete the OAuth consent flow afterward.
 *
 * Uses project-scoped sourceControlConnections routes.
 */
export async function createRepoConnection(
  params: CreateRepoConnectionParams,
  location: string,
): Promise<ConnectionResource> {
  const connectorId = buildConnectorId(params.connector.apiName);

  const accessToken = await params.ctx.getToken();
  return createConnection(
    {
      accessToken: accessToken ?? '',
      appId: `/automationProjects/${encodeURIComponent(params.ctx.projectName)}`,
      projectName: params.ctx.projectName,
      location,
    }, {
    connectionName: params.connectionName,
    connectorId,
    parameterValues: {},
    syncAfterCreation: false, // Defer syncing until after OAuth consent is completed
  }, true);
}

/**
 * Get the OAuth consent link for a repository connection (Step 2).
 *
 * Uses project-scoped sourceControlConnections routes.
 */
export async function getRepoConsentLink(
  params: RepoConsentParams,
  location: string,
): Promise<{ consentLink: string; firstPartyLoginUri?: string }> {
  const accessToken = await params.ctx.getToken();
  return listConsentLinks({
    accessToken: accessToken ?? '',
    appId: `/automationProjects/${encodeURIComponent(params.ctx.projectName)}`,
    projectName: params.ctx.projectName,
    location,
  },
  {
    connectionName: params.connectionName,
    redirectUrl: params.redirectUrl,
    objectId: params.objectId,
    tenantId: params.tenantId,
  }, true);
}

/**
 * Confirm the OAuth consent code for a repository connection (Step 4).
 *
 * Uses project-scoped sourceControlConnections routes.
 */
export async function confirmRepoConsent(
  params: RepoConfirmConsentParams,
  location: string,
): Promise<void> {
  const accessToken = await params.ctx.getToken();
  return confirmConsentCode({
    accessToken: accessToken ?? '',
    appId: `/automationProjects/${encodeURIComponent(params.ctx.projectName)}`,
    projectName: params.ctx.projectName,
    location,
  }, {
    connectionName: params.connectionName,
    code: params.code,
    objectId: params.objectId,
    tenantId: params.tenantId,
  }, true);
}

/**
 * Test a repository OAuth connection (Step 5).
 *
 * Uses project-scoped sourceControlConnections routes.
 */
export async function testRepoConnection(
  ctx: AppContext,
  connectionName: string,
  location: string,
): Promise<TestConnectionResult> {
  const accessToken = await ctx.getToken();
  return testConnection({
    accessToken: accessToken ?? '',
    appId: `/automationProjects/${encodeURIComponent(ctx.projectName)}`,
    projectName: ctx.projectName,
    location,
  }, connectionName, true);
}

/**
 * Detect the repo connector type from a repository URL.
 * Returns 'azuredevops' for ADO URLs, 'github' for GitHub URLs, or undefined
 * if the URL doesn't match a known pattern.
 */
export function detectConnectorType(repoUrl: string): RepoConnectorType | undefined {
  const url = repoUrl.toLowerCase();
  if (
    url.includes('dev.azure.com') ||
    url.includes('visualstudio.com') ||
    url.includes('ssh.dev.azure.com') ||
    url.includes('vs-ssh.visualstudio.com')
  ) {
    return 'azuredevops';
  }
  if (url.includes('github.com')) {
    return 'github';
  }
  return undefined;
}
